import React, { useEffect, useState, useCallback } from "react";
import { jwtDecode } from "jwt-decode";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faUserShield, faTimes, faPlus, faSpinner,
  faToggleOn, faToggleOff, faKey,
} from "@fortawesome/free-solid-svg-icons";
import AdminLayout from "./AdminLayout";
import "./admin.css";

function adminFetch(path, opts = {}) {
  const token = localStorage.getItem("adminToken");
  return fetch(path, {
    ...opts,
    headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json", ...(opts.headers || {}) },
  });
}

const ROLE_LABELS = {
  super_admin:  "Super Admin",
  kyc_verifier: "KYC Verifier",
  property_verifier: "Property Verifier",
  support:      "Support",
  technician:   "Technician",
};

function AddAdminPanel({ onClose, onDone }) {
  const [form,   setForm]   = useState({ email: "", password: "", firstname: "", lastname: "", admin_role: "kyc_verifier" });
  const [saving, setSaving] = useState(false);
  const [msg,    setMsg]    = useState({ type: "", text: "" });

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.email || !form.password || !form.firstname || !form.lastname) {
      setMsg({ type: "error", text: "All fields are required." });
      return;
    }
    if (form.password.length < 10) {
      setMsg({ type: "error", text: "Password must be at least 10 characters." });
      return;
    }
    setSaving(true);
    try {
      const res  = await adminFetch("/api/admin/team", { method: "POST", body: JSON.stringify(form) });
      const data = await res.json();
      if (res.ok) { setMsg({ type: "success", text: "Admin account created." }); onDone(); }
      else          setMsg({ type: "error",   text: data.message || "Failed." });
    } finally { setSaving(false); }
  };

  return (
    <div className="adm-panel-overlay" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="adm-panel">
        <div className="adm-panel-header">
          <h4>Add Admin Account</h4>
          <button className="adm-panel-close" onClick={onClose}><FontAwesomeIcon icon={faTimes} /></button>
        </div>
        <div className="adm-panel-body">
          {msg.text && <div className={`adm-alert ${msg.type}`}>{msg.text}</div>}
          <form onSubmit={submit}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: ".75rem" }}>
              <div className="adm-field">
                <label>First name *</label>
                <input value={form.firstname} onChange={(e) => set("firstname", e.target.value)} />
              </div>
              <div className="adm-field">
                <label>Last name *</label>
                <input value={form.lastname} onChange={(e) => set("lastname", e.target.value)} />
              </div>
            </div>
            <div className="adm-field">
              <label>Email *</label>
              <input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} />
            </div>
            <div className="adm-field">
              <label>Password * (min 10 chars)</label>
              <input type="password" value={form.password} onChange={(e) => set("password", e.target.value)} autoComplete="new-password" />
            </div>
            <div className="adm-field">
              <label>Role *</label>
              <select value={form.admin_role} onChange={(e) => set("admin_role", e.target.value)}>
                <option value="kyc_verifier">KYC Verifier</option>
                <option value="property_verifier">Property Verifier</option>
                <option value="support">Support</option>
                <option value="technician">Technician</option>
                <option value="super_admin">Super Admin</option>
              </select>
            </div>
            <button className="adm-btn primary" type="submit" disabled={saving} style={{ marginTop: ".25rem" }}>
              {saving ? <FontAwesomeIcon icon={faSpinner} spin /> : <FontAwesomeIcon icon={faPlus} />}
              Create Account
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}

function ResetPasswordPanel({ admin, onClose }) {
  const [pw,     setPw]     = useState("");
  const [saving, setSaving] = useState(false);
  const [msg,    setMsg]    = useState({ type: "", text: "" });

  const submit = async (e) => {
    e.preventDefault();
    if (pw.length < 10) { setMsg({ type: "error", text: "Password must be at least 10 characters." }); return; }
    setSaving(true);
    try {
      const res  = await adminFetch(`/api/admin/team/${admin.id}/reset-password`, {
        method: "PATCH", body: JSON.stringify({ password: pw }),
      });
      const data = await res.json();
      if (res.ok) setMsg({ type: "success", text: "Password reset successfully." });
      else          setMsg({ type: "error",   text: data.message || "Failed." });
    } finally { setSaving(false); }
  };

  return (
    <div className="adm-panel-overlay" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="adm-panel">
        <div className="adm-panel-header">
          <h4>Reset Password — {admin.firstname} {admin.lastname}</h4>
          <button className="adm-panel-close" onClick={onClose}><FontAwesomeIcon icon={faTimes} /></button>
        </div>
        <div className="adm-panel-body">
          {msg.text && <div className={`adm-alert ${msg.type}`}>{msg.text}</div>}
          <form onSubmit={submit}>
            <div className="adm-field">
              <label>New password (min 10 chars)</label>
              <input type="password" value={pw} onChange={(e) => setPw(e.target.value)} autoComplete="new-password" />
            </div>
            <button className="adm-btn warning" type="submit" disabled={saving}>
              {saving ? <FontAwesomeIcon icon={faSpinner} spin /> : <FontAwesomeIcon icon={faKey} />}
              Reset Password
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}

export default function AdminTeam() {
  const [rows,     setRows]     = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [showAdd,  setShowAdd]  = useState(false);
  const [resetFor, setResetFor] = useState(null);
  const [toggling, setToggling] = useState(null);
  const myId = (() => {
    try { return jwtDecode(localStorage.getItem("adminToken") || "")?.id; } catch { return null; }
  })();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res  = await adminFetch("/api/admin/team");
      const data = await res.json();
      if (res.ok) setRows(data);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggleActive = async (admin) => {
    if (admin.id === myId) return;
    if (!window.confirm(`${admin.is_active ? "Deactivate" : "Activate"} ${admin.firstname} ${admin.lastname}?`)) return;
    setToggling(admin.id);
    try {
      await adminFetch(`/api/admin/team/${admin.id}/toggle-active`, { method: "PATCH" });
      load();
    } finally { setToggling(null); }
  };

  return (
    <AdminLayout pageTitle="Admin Team">
      <div style={{ marginBottom: "1rem", display: "flex", justifyContent: "flex-end" }}>
        <button className="adm-btn primary" onClick={() => setShowAdd(true)}>
          <FontAwesomeIcon icon={faPlus} />
          Add Admin
        </button>
      </div>

      <div className="adm-card">
        <div className="adm-card-header">
          <h3>Team Members</h3>
          <span style={{ fontSize: ".8rem", color: "var(--adm-muted)" }}>{rows.length} accounts</span>
        </div>

        {loading && <div className="adm-spinner-wrap"><div className="spinner-border text-secondary" /></div>}

        {!loading && rows.length === 0 && (
          <div className="adm-empty"><FontAwesomeIcon icon={faUserShield} /><p>No team members found.</p></div>
        )}

        {!loading && rows.length > 0 && (
          <div className="adm-table-wrap">
            <table className="adm-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Role</th>
                  <th>Last Login</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((a) => (
                  <tr key={a.id}>
                    <td>
                      {a.firstname} {a.lastname}
                      {a.id === myId && <span style={{ fontSize: ".7rem", color: "var(--adm-teal)", marginLeft: ".35rem" }}>(you)</span>}
                    </td>
                    <td style={{ fontSize: ".82rem", color: "var(--adm-muted)" }}>{a.email}</td>
                    <td>
                      <span className={`adm-badge adm-role-badge ${a.admin_role}`}>
                        {ROLE_LABELS[a.admin_role] || a.admin_role}
                      </span>
                    </td>
                    <td style={{ fontSize: ".78rem", color: "var(--adm-muted)" }}>
                      {a.last_login_at ? new Date(a.last_login_at).toLocaleString() : "Never"}
                    </td>
                    <td>
                      <span className={`adm-badge ${a.is_active ? "approved" : "rejected"}`}>
                        {a.is_active ? "Active" : "Inactive"}
                      </span>
                    </td>
                    <td>
                      <div style={{ display: "flex", gap: ".4rem", flexWrap: "wrap" }}>
                        <button
                          className={`adm-btn sm ${a.is_active ? "danger" : "success"}`}
                          disabled={a.id === myId || toggling === a.id}
                          onClick={() => toggleActive(a)}
                          title={a.id === myId ? "Cannot deactivate yourself" : ""}
                        >
                          {toggling === a.id
                            ? <FontAwesomeIcon icon={faSpinner} spin />
                            : <FontAwesomeIcon icon={a.is_active ? faToggleOff : faToggleOn} />
                          }
                          {a.is_active ? "Deactivate" : "Activate"}
                        </button>
                        <button className="adm-btn warning sm" onClick={() => setResetFor(a)}>
                          <FontAwesomeIcon icon={faKey} />
                          Reset PW
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showAdd  && <AddAdminPanel  onClose={() => setShowAdd(false)}  onDone={() => { setShowAdd(false); load(); }} />}
      {resetFor && <ResetPasswordPanel admin={resetFor} onClose={() => setResetFor(null)} />}
    </AdminLayout>
  );
}
